import { Autocomplete, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, LinearProgress, Checkbox, FormControlLabel } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
// react-to-print v3 only exports useReactToPrint hook
import ExcelJS from "exceljs"
import './timeSales.css';
import { TrendingUp, TrendingDown, FileText, Clock, Download, Printer, HandCoins } from "lucide-react";
import SyncIcon from '@mui/icons-material/Sync';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { LineChart, Line, Area, Tooltip as RechartsTooltip, Legend } from 'recharts';
import PrintComponent from './PrintComponent';
import HourlySalesReport from './HourlySalesReport';
import { useSelector } from 'react-redux';
import { listTimeReportAPICall } from '../PointOfSale/Configuration/Settings/posSettingsAPI';
import { viewAllAllowedBranchesAPICall } from '../../../API/Settings/Company Settings/companySettingsAPI';
import { findObjFromList } from '../../../Js/generalFunctions';
import { viewShiftAPICall } from '../../../API/Settings/Shift/viewShiftAPI';

function TimeSalesReport() {

    const timeReportList = useSelector((state) => state.posSettingSlice.apiTimeReportList);
    const userRole = useSelector((state) => state.userRoleSlice.value);
    const allowedBranchList = useSelector((state) => state.allAllowedBranchesSlice.value);
    const allBranchesList = useSelector((state) => state.allBranchesSlice.value);
    const { branchDetails } = useSelector((state) => state.mainDashBoardSlice);
    const userInfo = useSelector((state) => state.loginResponseSlice.value);
    const allShiftList = useSelector((state) => state.viewShiftSlice.value);

    const [validationAlert, setValidationAlert] = useState({});
    const printRef = useRef();
    const [loading, setLoading] = useState(false);
    const [orderType, setOrderType] = useState('ALL');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('Net Sale');
    const [branch, setBranch] = useState(null);
    const [shift, setShift] = useState(null);
    const [shiftSelected, setShiftSelected] = useState(false); 
    const [showShiftSelected, setShowshiftSelected] = useState(false);
    const [useShiftDates, setUseShiftDates] = useState(false); // New state for checkbox
    const [hourlySalesOpen, setHourlySalesOpen] = useState(false);
    const [hourlySalesData, setHourlySalesData] = useState([]);

    const [timeRange, setTimeRange] = useState({
        start: '00:00',
        end: '23:59'
    });

    // Helper function to format time
    const formatTime = (timeString) => {
        if (!timeString) return '00:00';

        if (typeof timeString === 'string' && timeString.includes(':')) {
            const [hours, minutes] = timeString.split(':');
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        }

        const date = new Date(timeString);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };


    const downloadList = () => {
        if (!timeReportList?.data) {
            return;
        }

        // Create workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Time Sales Report");
        sheet.properties.defaultRowHeight = 20;

        let currentRow = 1;

        // Add title and header information
        const titleRow = sheet.addRow(["Time Sales Report"]);
        titleRow.font = { size: 16, bold: true };
        sheet.mergeCells(`A${currentRow}:E${currentRow}`);
        titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;

        // Add report details
        const details = [
            `Outlet: ${formValues.branch?.branchName || branchDetails?.branchName || 'All'}`,
            `Date: ${formValues.startDate ? formatDate(formValues.startDate) : 'Today'}${formValues.endDate && formValues.startDate !== formValues.endDate ? ` to ${formatDate(formValues.endDate)}` : ''}`,
            useShiftDates ? 'Date Mode: Shift-Based (Calculated from shifts within date range)' : 'Date Mode: Manual Date Range',
            `Time Range: ${timeRange.start} - ${timeRange.end}`,
            `Order Type: ${orderTypeOptions.find(opt => opt.value === orderType)?.label || 'All'}`,
            shift ? `Shift: ${shift.shiftSeq}` : ''
        ];

        details.forEach((detail) => {
            if (detail) { // Only add non-empty details
                const row = sheet.addRow([detail]);
                sheet.mergeCells(`A${currentRow}:E${currentRow}`);
                row.getCell(1).font = { bold: true };
                currentRow++;
            }
        });

        // Add empty row
        sheet.addRow([]);
        currentRow++;

        // Add metrics section
        const metricsTitle = sheet.addRow(["Metrics"]);
        metricsTitle.font = { size: 14, bold: true };
        sheet.mergeCells(`A${currentRow}:E${currentRow}`);
        metricsTitle.getCell(1).alignment = { horizontal: 'center' };
        currentRow++;

        const metrics = getMetrics(timeReportList.data);
        const metricsData = [
            ["Metric", "Value"],
            ...metrics.map(metric => [metric.title, metric.value])
        ];

        metricsData.forEach((rowData, index) => {
            const row = sheet.addRow(rowData);
            currentRow++;
            if (index === 0) {
                // Header row styling
                row.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFedf2f9" }
                    };
                    cell.font = { bold: true };
                    cell.alignment = { horizontal: 'center' };
                });
            }
        });

        // Add empty row
        sheet.addRow([]);
        currentRow++;

        // Add Time-wise Sales section if data exists
        if (timeReportList.data.timeGraph?.length > 0) {
            const timeSalesTitle = sheet.addRow(["Time-wise Sales"]);
            timeSalesTitle.font = { size: 14, bold: true };
            sheet.mergeCells(`A${currentRow}:E${currentRow}`);
            timeSalesTitle.getCell(1).alignment = { horizontal: 'center' };
            currentRow++;

            const timeSalesHeader = sheet.addRow(["Hour", "Total Sales"]);
            timeSalesHeader.eachCell(cell => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFedf2f9" }
                };
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center' };
            });

            timeReportList.data.timeGraph.forEach(item => {
                sheet.addRow([item.hour, `Rs ${item.value.toFixed(2)}`]);
                currentRow++;
            });

            // Add empty row
            sheet.addRow([]);
            currentRow++;
        }

        // Add Slot Summary section if data exists
        if (timeReportList.data.slotSummary?.length > 0) {
            const slotSummaryTitle = sheet.addRow(["Slot Summary"]);
            slotSummaryTitle.font = { size: 14, bold: true };
            sheet.mergeCells(`A${currentRow}:E${currentRow}`);
            slotSummaryTitle.getCell(1).alignment = { horizontal: 'center' };
            currentRow++;
            const slotSummaryHeaders = ["Time Slot", "Bills", "Avg. Value", "Gross Sale", "Net Sale"];
            const slotSummaryHeaderRow = sheet.addRow(slotSummaryHeaders);
            slotSummaryHeaderRow.eachCell(cell => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFedf2f9" }
                };
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center' };
            });

            timeReportList.data.slotSummary.forEach(item => {
                sheet.addRow([
                    item.slot,
                    item.bills,
                    `Rs ${item.avgValue}`,
                    `Rs ${item.grossSale.toFixed(2)}`,
                    `Rs ${item.netSale?.toFixed(2) || item.grossSale.toFixed(2)}`
                ]);
                currentRow++;
            });

            // Add totals if available
            if (timeReportList.data.slotSummaryTotal) {
                const totalRow = sheet.addRow([
                    "Total",
                    timeReportList.data.slotSummaryTotal.totalBillSum,
                    `Rs ${timeReportList.data.slotSummaryTotal.totalAvgValueSum}`,
                    `Rs ${timeReportList.data.slotSummaryTotal.totalGrossSaleSum.toFixed(2)}`,
                    `Rs ${timeReportList.data.slotSummaryTotal.totalGrossSaleSum.toFixed(2)}`
                ]);
                currentRow++;
                totalRow.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFb7b6b6" }
                    };
                    cell.font = { bold: true };
                    cell.alignment = { horizontal: 'center' };
                });
            }

            // Add empty row
            sheet.addRow([]);
            currentRow++;
        }

        // Add Top Selling Products section if data exists
        if (timeReportList.data.topProducts?.length > 0) {
            const topProductsTitle = sheet.addRow(["Top Selling Products"]);
            topProductsTitle.font = { size: 14, bold: true };
            sheet.mergeCells(`A${currentRow}:E${currentRow}`);
            topProductsTitle.getCell(1).alignment = { horizontal: 'center' };
            currentRow++;

            const topProductsHeaders = ["Time Slot", "Product", "Qty", "UOM", "Net Sale"];
            const topProductsHeaderRow = sheet.addRow(topProductsHeaders);
            topProductsHeaderRow.eachCell(cell => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFedf2f9" }
                };
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center' };
            });

            timeReportList.data.topProducts.forEach(item => {
                sheet.addRow([
                    item.timeSlot,
                    item.productName,
                    item.qty,
                    item.uomCat,
                    `Rs ${item.netSale.toFixed(2)}`
                ]);
                currentRow++;
            });

            // Add empty row
            sheet.addRow([]);
            currentRow++;
        }

        // Add Report Type Data section if data exists
        if (timeReportList.data.reportTypeData) {
            const reportTypeTitle = sheet.addRow([`${selectedTab} Details`]);
            reportTypeTitle.font = { size: 14, bold: true };
            sheet.mergeCells(`A${currentRow}:E${currentRow}`);
            reportTypeTitle.getCell(1).alignment = { horizontal: 'center' };
            currentRow++;

            let reportTypeHeaders = [];
            let reportTypeData = [];

            if (selectedTab === 'Net Sale') {
                reportTypeHeaders = ["Type", "Amount"];
                reportTypeData = [
                    ["Dine in sale", timeReportList.data.reportTypeData.dineInSale || 0],
                    ["Take away sale", timeReportList.data.reportTypeData.takeAwaySale || 0],
                    ["Delivery Sale", timeReportList.data.reportTypeData.deliverySale || 0]
                ];
            } else if (selectedTab === 'Total Transaction') {
                reportTypeHeaders = ["Transaction Type", "Count"];
                reportTypeData = [
                    ["Dine in Txn", timeReportList.data.reportTypeData.dineInTxn || 0],
                    ["Take away Txn", timeReportList.data.reportTypeData.takeAwayTxn || 0],
                    ["Delivery Txn", timeReportList.data.reportTypeData.deliveryTxn || 0]
                ];
            } else if (selectedTab === 'Apc') {
                reportTypeHeaders = ["APC Type", "Value"];
                reportTypeData = [
                    ["Average Per Customer", timeReportList.data.reportTypeData.averagePerCustomer || '0.00'],
                    ["Dine in APC", timeReportList.data.reportTypeData.dineInAPC || '0.00'],
                    ["Take away APC", timeReportList.data.reportTypeData.takeAwayAPC || '0.00'],
                    ["Delivery APC", timeReportList.data.reportTypeData.deliveryAPC || '0.00']
                ];
            }

            if (reportTypeHeaders.length > 0) {
                const reportTypeHeaderRow = sheet.addRow(reportTypeHeaders);
                reportTypeHeaderRow.eachCell(cell => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFedf2f9" }
                    };
                    cell.font = { bold: true };
                    cell.alignment = { horizontal: 'center' };
                });

                reportTypeData.forEach(row => {
                    sheet.addRow(row);
                    currentRow++;
                });
            }
        }

        // Auto-fit columns
        sheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                let columnLength = cell.value ? cell.value.toString().length : 10;
                if (columnLength > maxLength) {
                    maxLength = columnLength;
                }
            });
            column.width = Math.min(Math.max(maxLength + 2, 10), 30);
        });

        // Generate dynamic filename
        const branchName = (formValues.branch?.branchName || branchDetails?.branchName || 'All').replace(/[^a-z0-9]/gi, '_');
        const startDate = formValues.startDate || getTodayDate();
        const endDate = formValues.endDate || getTodayDate();
        const dateRange = startDate === endDate ? startDate : `${startDate}_to_${endDate}`;
        const shiftName = shift ? `_${shift.shiftSeq.replace(/[^a-z0-9]/gi, '_')}` : '';
        const fileName = `TimeSalesReport_${branchName}_${dateRange}${shiftName}.xlsx`;

        // Generate and download the Excel file
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = fileName;
            anchor.click();
            window.URL.revokeObjectURL(url);
        });
    };


    // Generate time options with 30-minute intervals

    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push(timeString);
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    const TimeRangeSelector = ({ value, onChange }) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FormControl style={{ minWidth: '120px' }}>
                    <InputLabel>{value.start !== '00:00' ? value.start : 'Start Time'}</InputLabel>
                    <Select
                        value={value.start == '00:00' ? value.start : ''}
                        onChange={(e) => onChange('start', e.target.value)}
                        label="Start Time"
                    >
                        {timeOptions.map((time, index) => (
                            <MenuItem key={`start-${time}`}
                                value={time}> {time}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <span>to</span>
                <FormControl style={{ minWidth: '120px' }}>
                    <InputLabel>{value.end !== '00:00' ? value.end : 'End Time'}</InputLabel>
                    {/* <InputLabel>End Time</InputLabel> */}
                    <Select
                        value={value.end == '00:00' ? value.end : ''}
                        onChange={(e) => onChange('end', e.target.value)}
                        label="End Time"
                    >
                        {timeOptions.map((time, index) => (
                            <MenuItem key={`end-${time}`} value={time}>{time}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        );
    };

    const handleTimeRangeChange = (type, value) => {
        // if (shiftSelected) {
        //     // Reset shift selection when manually changing time
        //     setShift(null);
        //     setShiftSelected(false);
        // }
        setTimeRange(prev => ({
            ...prev,
            [type]: value
        }));
        setShowshiftSelected(false)
    };

    const orderTypeOptions = [
        { value: 'ALL', label: 'All' },
        { value: 'DINEIN', label: 'Dine In' },
        { value: 'TAKEAWAY', label: 'Take Away' },
        { value: 'DELIVERY', label: 'Delivery' }
    ];

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formInitialState = {
        branch: branchDetails,
        startDate: getTodayDate(),
        endDate: getTodayDate(),
        shift: '',
        timeRange: ''
    };

    const [formValues, setFormValues] = useState(() => ({
        ...formInitialState,
        startDate: getTodayDate(),
        endDate: getTodayDate()
    }));

    const getProductOptions = () => {
        if (!timeReportList || !timeReportList.data || !timeReportList.data.topProducts || !Array.isArray(timeReportList.data.topProducts)) {
            return [];
        }
        return timeReportList.data.topProducts.map(product => ({
            value: product?._id || '',
            label: product?.productName || 'Unknown Product'
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`;
    };

    const resetForm = () => {
        setFormValues({
            ...formInitialState,
            startDate: getTodayDate(),
            endDate: getTodayDate()
        });
        setOrderType('ALL');
        setSelectedProduct(null);
        setTimeRange({ start: '00:00', end: '23:59' });
        setValidationAlert({});
        setShift(null);
        setShiftSelected(false);
        setShowshiftSelected(false);
        setUseShiftDates(false); // Reset checkbox
    };

    const getDayRange = (startDate, endDate) => {
        // Ensure we're working with date strings in YYYY-MM-DD format
        const startDateStr = startDate || getTodayDate();
        const endDateStr = endDate || getTodayDate();
        
        const [startHour, startMinute] = timeRange.start.split(':').map(Number);
        let [endHour, endMinute] = timeRange.end.split(':').map(Number);
        
        // If end time is 00:00, it means end of day (23:59)
        if (endHour === 0 && endMinute === 0) {
            endHour = 23;
            endMinute = 59;
        }

        const formatLocalDateTime = (dateStr, hours, minutes, seconds = 0) => {
            return `${dateStr} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        };

        const fromDateTime = formatLocalDateTime(startDateStr, startHour, startMinute, 0);
        const toDateTime = formatLocalDateTime(endDateStr, endHour, endMinute, 59);

        return {
            fromDate: fromDateTime,
            endDate: toDateTime,
            timeRange: `${timeRange.start} - ${timeRange.end}`
        };
    };

    const getFormInfo = (key) => (e) => {
        const { value } = e?.target || {};
        
        // Validate end date is not before start date
        if (key === 'endDate' && formValues.startDate && value < formValues.startDate) {
            setValidationAlert({ endDate: "End date cannot be before start date" });
            return;
        }
        
        // Validate start date is not after end date
        if (key === 'startDate' && formValues.endDate && value > formValues.endDate) {
            // Auto-adjust end date to match start date
            setFormValues({
                ...formValues,
                startDate: value,
                endDate: value
            });
        } else {
            setFormValues({
                ...formValues,
                [key]: value
            });
        }
        
        if (shiftSelected && (key === 'startDate' || key === 'endDate')) {
            // Reset shift selection when manually changing date
            setShift(null);
            setShiftSelected(false);
            setShowshiftSelected(false);
        }
        
        // Reset time range to full day when manually changing dates
        if (key === 'startDate' || key === 'endDate') {
            setTimeRange({ start: '00:00', end: '23:59' });
        }
        
        setValidationAlert({});
        
        // Auto-submit when date changes if branch is selected
        if ((branch || formValues.branch) && value) {
            debouncedSubmit();
        }
    };

const handleBranchChange = async (e, newValue) => {
        if (e) e.preventDefault();
        setValidationAlert({});
        setFormValues({
            ...formValues,
            branch: newValue
        });
        setShift(null);
        setShiftSelected(false);
        setShowshiftSelected(false);
        setTimeRange({ start: '00:00', end: '23:59' }); // Reset to full day
        
        // Auto-submit when branch changes if dates are set
        if (newValue && formValues.startDate) {
            debouncedSubmit();
        }
    };

    const handleTimeChange = (key) => (e) => {
        setTimeRange({
            ...timeRange,
            [key]: e.target.value
        });
    };

    const handleOrderTypeChange = (e, newValue) => {
        if (e) e.preventDefault();
        setOrderType(newValue?.value || 'ALL');
        
        // Auto-submit when order type changes if branch and dates are set
        if ((branch || formValues.branch) && formValues.startDate) {
            debouncedSubmit();
        }
    };

    const handleProductChange = async (e) => {
        const productId = e.target.value || null;
        setSelectedProduct(productId);

        if ((branch || formValues.branch)?._id && formValues.startDate && formValues.endDate) {
            setLoading(true);
            try {
                const { fromDate, endDate, timeRange: formattedTimeRange } = getDayRange(formValues.startDate, formValues.endDate);
                await listTimeReportAPICall({
                    branchId: branch?._id || formValues.branch?._id,
                    fromDate,
                    endDate,
                    orderType: orderType === 'ALL' ? '' : orderType,
                    selectedProductId: productId,
                    reportType: selectedTab,
                    timeRange: formattedTimeRange,
                    shiftId: shift?._id,
                    selectShift: showShiftSelected,
                    useShiftDates: useShiftDates
                });
            } catch (error) {
                console.error("Product change error:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleTabChange = async (tab) => {
        setSelectedTab(tab);

        if ((branch || formValues.branch)?._id && formValues.startDate && formValues.endDate) {
            setLoading(true);
            try {
                const { fromDate, endDate } = getDayRange(formValues.startDate, formValues.endDate);
                await listTimeReportAPICall({
                    branchId: branch?._id || formValues.branch?._id,
                    fromDate,
                    endDate,
                    orderType: orderType === 'ALL' ? '' : orderType,
                    selectedProductId: selectedProduct,
                    reportType: tab,
                    timeRange: `${timeRange.start} - ${timeRange.end}`,
                    shiftId: shift?._id,
                    selectShift: showShiftSelected,
                    useShiftDates: useShiftDates
                });
            } catch (error) {
                console.error("Error in tab change:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleShiftChange = (e, newValue) => {
        if (e) e.preventDefault();
        setShift(newValue);
        setShiftSelected(!!newValue); // Set to true when a shift is selected
        setShowshiftSelected(!!newValue); // Show badge when shift is selected

        if (newValue) {
            // Format the shift times to match the timeOptions format (HH:MM)
            const formatShiftTime = (timeString) => {
                if (!timeString) return '00:00';
                // If already in HH:MM format
                if (typeof timeString === 'string' && timeString.includes(':')) {
                    const [hours, minutes] = timeString.split(':');
                    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                }
                // If it's a timestamp or Date object
                const date = new Date(timeString);
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${hours}:${minutes}`;
            };

            setTimeRange({
                start: formatShiftTime(newValue.startTime),
                end: formatShiftTime(newValue.endTime)
            });
            
            // Auto-submit when shift changes if branch is set
            if (branch || formValues.branch) {
                debouncedSubmit();
            }
        }
    };

    // ... (rest of the component remains the same, just replace the handleShiftChange function)

    // debounce ref for auto-submit from UI changes
    const submitTimerRef = useRef(null);

    useEffect(() => {
        return () => { if (submitTimerRef.current) clearTimeout(submitTimerRef.current); };
    }, []);

    const debouncedSubmit = () => {
        if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
        submitTimerRef.current = setTimeout(() => {
            handleSubmit();
        }, 350);
    };

    const handleSubmit = async () => {
        if (!branch && !formValues.branch) {
            setValidationAlert({ branch: "Please select a branch" });
            return;
        }

        if (!formValues.startDate) {
            setValidationAlert({ startDate: "Please select start date" });
            return;
        }

        setLoading(true);
        
        try {
            const startDate = formValues.startDate || getTodayDate();
            const endDate = formValues.endDate || startDate;

            const { fromDate, endDate: apiEndDate } = getDayRange(startDate, endDate);

            const params = {
                branchId: (branch?._id || formValues.branch?._id),
                fromDate,
                endDate: apiEndDate,
                orderType: orderType === 'ALL' ? '' : orderType,
                selectedProductId: selectedProduct,
                reportType: selectedTab,
                timeRange: `${timeRange.start} - ${timeRange.end}`,
                shiftId: shift?._id,
                selectShift: showShiftSelected,
                useShiftDates: useShiftDates
            };

            console.log('[TIME REPORT] Submitting with params:', params);
            await listTimeReportAPICall(params);
        } catch (error) {
            console.error("[TIME REPORT] API Error:", error);
            setValidationAlert({ api: "Failed to fetch report data" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let stale = false;

        const loadInitialData = async () => {
            try {
                setInitialLoading(true);
                const currentBranch = branch || formValues.branch || branchDetails;

                if (!currentBranch?._id) {
                    setInitialLoading(false);
                    return;
                }

                // Load shifts for the current branch
                await viewShiftAPICall({ branchId: currentBranch._id });

                if (stale) return;

                // Call API directly with known branch — don't rely on handleSubmit
                // because state closures may not yet reflect currentBranch
                const startDate = formValues.startDate || getTodayDate();
                const endDate = formValues.endDate || startDate;
                if (startDate && endDate) {
                    setLoading(true);
                    try {
                        const { fromDate, endDate: apiEndDate } = getDayRange(startDate, endDate);
                        await listTimeReportAPICall({
                            branchId: currentBranch._id,
                            fromDate,
                            endDate: apiEndDate,
                            orderType: orderType === 'ALL' ? '' : orderType,
                            selectedProductId: selectedProduct,
                            reportType: selectedTab,
                            timeRange: `${timeRange.start} - ${timeRange.end}`,
                            shiftId: shift?._id,
                            selectShift: showShiftSelected,
                            useShiftDates: useShiftDates
                        });
                    } finally {
                        if (!stale) setLoading(false);
                    }
                }
            } catch (error) {
                console.error("Error loading initial data:", error);
            } finally {
                if (!stale) setInitialLoading(false);
            }
        };

        loadInitialData();
        return () => { stale = true; };
    }, [branch, formValues.branch, branchDetails]);

    useEffect(() => {
        userRole === "user" && viewAllAllowedBranchesAPICall({ employeeId: userInfo?.data?._id });
    }, [userRole, userInfo]);

    useEffect(() => {
        if (!branchDetails && allBranchesList && allBranchesList.length > 0) {
            setFormValues(prev => ({
                ...prev,
                branch: allBranchesList[0]
            }));

        }
    }, [formValues.branch, allBranchesList]);

    const getMetrics = (data) => {
        if (!data) return [];

        return [
            {
                title: "Total Sales",
                value: `Rs ${data.totalSales?.toFixed(2) || '0.00'}`,
                icon: HandCoins,
                color: "green",
            },
            {
                title: "Total Bills",
                value: data.totalBills || 0,
                icon: FileText,
                color: "blue",
            },
            {
                title: "Avg. Bill Value",
                value: `Rs ${data.avgBillValue || '0.00'}`,
                icon: FileText,
                color: "green",
            },
            {
                title: "Top Performing",
                value: data.topPerformingTime || '0',
                icon: Clock,
                color: "yellow",
            },
        ];
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip" style={{
                    background: 'white',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                            <p style={{ color: '#10b981' }}>Total Sales:</p>
                            <p>Rs {payload[0].value.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    const LineCustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const productName = payload[0]?.payload?.productName || 'All Products';
            return (
                <div className="custom-tooltip" style={{
                    background: 'white',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
                    <p style={{ color: '#666', marginBottom: '8px' }}>Product: {productName}</p>
                    <div>
                        <p style={{ color: '#10b981' }}>Value:</p>
                        <p>Rs {payload[0].value.toFixed(2)}</p>
                    </div>
                </div>
            );
        }
        return null;
    };


    if (initialLoading) {
        return (
            <div className="global-page-parent-container" style={{ position: "relative" }}>
                <div className="global-white-bg-container" style={{ marginTop: "20px", padding: "20px", textAlign: 'center' }}>
                    <p>Loading initial data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="global-page-parent-container" style={{ position: "relative" }}>
            <div className="global-white-bg-container" ref={printRef} style={{ marginTop: "20px", padding: "20px" }}>
                {/* Form Controls */}
                <div className="global-white-bg-container" style={{ display: "flex", alignItems: "center", gap: "2%", flexWrap: "wrap" }}>
                    {/* Branch Selector */}
                    <div style={{ width: '18%', minWidth: '200px' }}>
                        <Autocomplete
                            disablePortal
                            id="branch-select"
                            options={userRole === "admin" ? allBranchesList : allowedBranchList || []}
                            getOptionLabel={(option) => `${option?.storeCode}-${option?.branchName}`}
                            renderInput={(params) => (
                                <TextField {...params} label="Outlet*" focused />
                            )}
                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    {`${option?.storeCode}-${option?.branchName}`}
                                </li>
                            )}
                            onChange={handleBranchChange}
                            value={formValues.branch || null}
                            componentsProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'preventOverflow',
                                            options: {
                                                boundary: 'viewport',
                                            },
                                        },
                                    ],
                                },
                            }}
                        />
                        <p className="doc-validation-alert">{validationAlert?.branch}</p>
                    </div>

                    {/* Shift Selector */}
                    <div style={{ width: '15%', minWidth: '180px' }}>
                        <Autocomplete
                            disablePortal
                            id="shift-select"
                            options={allShiftList?.filter(s => s.branchId === formValues.branch?._id) || []}
                            getOptionLabel={(option) => option?.shiftSeq || option?.SHIFID}
                            renderInput={(params) => (
                                <TextField {...params} label="Shift" focused />
                            )}
                            onChange={handleShiftChange}
                            value={shift || null}
                            disabled={!formValues.branch}
                            componentsProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'preventOverflow',
                                            options: {
                                                boundary: 'viewport',
                                            },
                                        },
                                    ],
                                },
                            }}
                        />
                    </div>

                    {/* Use Shift Dates Checkbox */}
                    <div style={{ width: '12%', minWidth: '140px', display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useShiftDates}
                                    onChange={async (e) => {
                                        const newValue = e.target.checked;
                                        setUseShiftDates(newValue);
                                        
                                        // Trigger re-submit with new value
                                        if (formValues.branch && formValues.startDate && formValues.endDate) {
                                            setLoading(true);
                                            try {
                                                const startDate = formValues.startDate || getTodayDate();
                                                const endDate = formValues.endDate || startDate;
                                                const { fromDate, endDate: apiEndDate } = getDayRange(startDate, endDate);
                                                
                                                await listTimeReportAPICall({
                                                    branchId: (branch?._id || formValues.branch?._id),
                                                    fromDate,
                                                    endDate: apiEndDate,
                                                    orderType: orderType === 'ALL' ? '' : orderType,
                                                    selectedProductId: selectedProduct,
                                                    reportType: selectedTab,
                                                    timeRange: `${timeRange.start} - ${timeRange.end}`,
                                                    shiftId: shift?._id,
                                                    selectShift: showShiftSelected,
                                                    useShiftDates: newValue  // Use the new value directly
                                                });
                                            } catch (error) {
                                                console.error("Checkbox change error:", error);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        color: '#10b981',
                                        '&.Mui-checked': {
                                            color: '#10b981',
                                        },
                                    }}
                                />
                            }
                            label={<span style={{ fontSize: '12px', color: '#666' }}>Use Shift Dates</span>}
                            sx={{ margin: 0 }}
                        />
                    </div>

                    {/* Start Date Input */}
                    <div style={{ width: '13%', minWidth: '150px' }}>
                        <TextField
                            label="Start Date*"
                            type="date"
                            variant='outlined'
                            value={formValues.startDate || getTodayDate()}
                            onChange={getFormInfo("startDate")}
                            InputLabelProps={{ 
                                shrink: true,
                                focused: true
                            }}
                            InputProps={{
                                style: { 
                                    backgroundColor: 'white', 
                                    borderRadius: '4px' 
                                },
                                inputProps: {
                                    max: getTodayDate() // Prevent future dates
                                }
                            }}
                            focused
                        />
                        <p className="doc-validation-alert">{validationAlert?.startDate}</p>
                    </div>

                    {/* End Date Input */}
                    <div style={{ width: '13%', minWidth: '150px' }}>
                        <TextField
                            label="End Date*"
                            type="date"
                            variant='outlined'
                            value={formValues.endDate || getTodayDate()}
                            onChange={getFormInfo("endDate")}
                            InputLabelProps={{
                                shrink: true,
                                focused: true
                            }}
                            InputProps={{
                                style: { 
                                    backgroundColor: 'white', 
                                    borderRadius: '4px' 
                                },
                                inputProps: {
                                    min: formValues.startDate || getTodayDate(), // End date cannot be before start date
                                    max: getTodayDate() // Prevent future dates
                                }
                            }}
                            focused
                        />
                        <p className="doc-validation-alert">{validationAlert?.endDate}</p>
                    </div>

                    {/* Order Type Selector */}
                    <div style={{ width: '15%', minWidth: '180px' }}>
                        <Autocomplete
                            options={orderTypeOptions}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Order Type"
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { backgroundColor: 'white', borderRadius: '4px' }
                                    }}
                                />
                            )}
                            onChange={handleOrderTypeChange}
                            value={orderTypeOptions.find(opt => opt.value === orderType) || null}
                        />
                    </div>

                    {/* Time Range Filter */}
                    <div style={{ width: '12%', minWidth: '140px' }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <TextField
                                label="From"
                                type="time"
                                value={timeRange.start}
                                onChange={(e) => setTimeRange({ ...timeRange, start: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 1800 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ width: '12%', minWidth: '140px' }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <TextField
                                label="To"
                                type="time"
                                value={timeRange.end}
                                onChange={(e) => setTimeRange({ ...timeRange, end: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 1800 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    {/* Hourly Sales Button */}
                    <div style={{ width: '10%', minWidth: '120px', display: 'flex', alignItems: 'center' }}>
                        <button
                            onClick={() => {
                                console.log("=== HOURLY SALES BUTTON CLICKED ===");
                                console.log("Time Report List Data:", timeReportList?.data);
                                console.log("Hourly Data:", timeReportList?.data?.hourlyData);
                                console.log("Hourly Data Length:", timeReportList?.data?.hourlyData?.length);
                                setHourlySalesOpen(true);
                            }}
                            disabled={loading || !timeReportList?.data}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: (loading || !timeReportList?.data) ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                opacity: (loading || !timeReportList?.data) ? 0.6 : 1,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            📊 Hourly Sales
                        </button>
                    </div>
                </div>

                {/* Loading Indicator */}
                {loading && (
                    <div style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
                        <LinearProgress 
                            sx={{
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#10b981'
                                }
                            }}
                        />
                        <p style={{ textAlign: 'center', marginTop: '8px', color: '#666', fontSize: '14px' }}>
                            Loading report data...
                        </p>
                    </div>
                )}

                {/* Metrics Section */}
                <div className="metrics-grid" style={{ position: 'relative', opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    {timeReportList?.data ? (
                        getMetrics(timeReportList.data).map((metric, index) => {
                            const IconComponent = metric.icon;
                            return (
                                <div key={index} className="metric-card">
                                    <div className="metric-header">
                                        <h3 className="metric-title">{metric.title}</h3>
                                        <IconComponent className={`metric-icon ${metric.color}`} />
                                    </div>
                                    <div className="metric-footer">
                                        <div>
                                            <p className="metric-value">{metric.value}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-data-message">No metrics data available</div>
                    )}
                </div>

                {/* Time-wise Sales Chart */}
                {timeReportList?.data?.timeGraph?.length > 0 ? (
                    <div className="chart-container">
                        <div className="chart-header">
                            <h3 className="chart-title">Time wise sales</h3>
                        </div>
                        <div style={{ height: '320px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={timeReportList.data.timeGraph}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="hour"
                                        tick={{ fontSize: 12 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Bar
                                        dataKey="value"
                                        name="Total Sales"
                                        fill="#10b981"
                                        radius={[2, 2, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ) : (
                    <div className="no-data-message" style={{ textAlign: 'center', padding: '50px 20px 10px', color: '#9999b3' }}>No data available</div>
                )}

                {/* Slot Summary Table */}
                {timeReportList?.data?.slotSummary?.length > 0 ? (
                    <div className="new-global-table-container"
                        style={{
                            marginTop: "20px",
                            maxHeight: "39vh",
                            borderRadius: "8px",
                            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                            border: "1px solid #e5e7eb",
                        }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Time Slot</th>
                                    <th>Bills</th>
                                    <th>Avg. Value</th>
                                    <th>Gross Sale</th>
                                    <th>Net Sale</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeReportList.data.slotSummary.map((row, index) => (
                                    <tr key={index} className="table-row">
                                        <td>{row.slot}</td>
                                        <td>{row.bills}</td>
                                        <td>Rs {row.avgValue}</td>
                                        <td>Rs {row.grossSale.toFixed(2)}</td>
                                        <td>Rs {row.grossSale.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            {timeReportList.data.slotSummaryTotal && (
                                <tfoot>
                                    <tr style={{ backgroundColor: "#b7b6b6", textAlign: "center" }}>
                                        <td>Total</td>
                                        <td>{timeReportList.data.slotSummaryTotal.totalBillSum}</td>
                                        <td>Rs {timeReportList.data.slotSummaryTotal.totalAvgValueSum}</td>
                                        <td>Rs {timeReportList.data.slotSummaryTotal.totalGrossSaleSum.toFixed(2)}</td>
                                        <td>Rs {timeReportList.data.slotSummaryTotal.totalGrossSaleSum.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                ) : (
                    <div className="no-data-message"></div>
                )}

                {/* Products Section - 3 Column Layout */}
                <div className="products-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '20px',
                    marginTop: '20px'
                }}>
                    {/* Column 1: Top Selling Products */}
                    <div className="chart-container">
                        <div className="products-header">
                            <h3 className="chart-title">Top Selling Products</h3>
                        </div>
                        {timeReportList?.data?.topProducts?.length > 0 ? (
                            <div className="new-global-table-container"
                                style={{
                                    marginTop: "20px",
                                    maxHeight: "24vh",
                                    borderRadius: "8px",
                                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                    border: "1px solid #e5e7eb",
                                }}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Time Slot</th>
                                            <th>Product</th>
                                            <th>Qty</th>
                                            <th>UOM</th>
                                            <th>Net Sale</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {timeReportList.data.topProducts.map((product, index) => (
                                            <tr key={index} className="table-row">
                                                <td>{product.timeSlot}</td>
                                                <td>{product.productName}</td>
                                                <td>{product.qty}</td>
                                                <td>{product.uomCat}</td>
                                                <td>Rs {product.netSale.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data-message" style={{
                                padding: '40px',
                                textAlign: 'center',
                                fontSize: '16px',
                                color: '#999',
                                borderRadius: '8px',
                                margin: '20px 0',
                                border: '1px dashed #ddd'
                            }}>No data available</div>
                        )}
                        <div className="products-actions">
                            <button
                                className="action-button excel"
                                onClick={downloadList}
                                disabled={!timeReportList?.data}
                            >
                                <span>📊</span>
                                <span>Export Excel</span>
                            </button>
                            <button className="action-button">
                                🖨️
                            </button>
                        </div>
                    </div>

                    {/* Column 2: Tabbed Section */}
                    <div className="chart-container">
                        {/* Tab Navigation */}
                        <div className="tab-navigation" style={{
                            display: 'flex',
                            borderBottom: '1px solid #e5e7eb',
                            marginBottom: '15px'
                        }}>
                            {['Net Sale', 'Total Transaction', 'Apc'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`tab-button ${selectedTab === tab ? 'active' : 'Net Sale'}`}
                                    onClick={() => handleTabChange(tab)}
                                    style={{
                                        padding: '10px 15px',
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        borderBottom: selectedTab === tab ? '2px solid #3b82f6' : 'none',
                                        color: selectedTab === tab ? '#3b82f6' : '#333',
                                        fontWeight: selectedTab === tab ? '500' : '400'
                                    }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {selectedTab === 'Net Sale' && (
                                <div className="new-global-table-container"
                                    style={{
                                        marginTop: "0",
                                        maxHeight: "24vh",
                                        borderRadius: "8px",
                                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                        border: "1px solid #e5e7eb",
                                    }}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Net Sales</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="table-row">
                                                <td>Dine in sale</td>
                                                <td>{timeReportList?.data?.reportTypeData?.dineInSale || 0}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Take away sale</td>
                                                <td>{timeReportList?.data?.reportTypeData?.takeAwaySale || 0}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Delivery Sale</td>
                                                <td>{timeReportList?.data?.reportTypeData?.deliverySale || 0}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedTab === 'Total Transaction' && (
                                <div className="new-global-table-container"
                                    style={{
                                        marginTop: "0",
                                        maxHeight: "24vh",
                                        borderRadius: "8px",
                                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                        border: "1px solid #e5e7eb",
                                    }}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Transaction Type</th>
                                                <th>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="table-row">
                                                <td>Dine in Txn</td>
                                                <td>{timeReportList?.data?.reportTypeData?.dineInTxn || 0}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Take away Txn</td>
                                                <td>{timeReportList?.data?.reportTypeData?.takeAwayTxn || 0}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Delivery Txn</td>
                                                <td>{timeReportList?.data?.reportTypeData?.deliveryTxn || 0}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedTab === 'Apc' && (
                                <div className="new-global-table-container"
                                    style={{
                                        marginTop: "0",
                                        maxHeight: "24vh",
                                        borderRadius: "8px",
                                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                        border: "1px solid #e5e7eb",
                                    }}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>APC Type</th>
                                                <th>Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="table-row">
                                                <td>Average Per Customer</td>
                                                <td>{timeReportList?.data?.reportTypeData?.averagePerCustomer || '0.00'}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Dine in APC</td>
                                                <td>{timeReportList?.data?.reportTypeData?.dineInAPC || '0.00'}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Take away APC</td>
                                                <td>{timeReportList?.data?.reportTypeData?.takeAwayAPC || '0.00'}</td>
                                            </tr>
                                            <tr className="table-row">
                                                <td>Delivery APC</td>
                                                <td>{timeReportList?.data?.reportTypeData?.deliveryAPC || '0.00'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Column 3: Product Graph */}
                    <div className="chart-container">
                        <select
                            style={{ marginBottom: "25px", padding: "10px", width: "300px" }}
                            className="products-filter"
                            value={selectedProduct || ''}
                            onChange={handleProductChange}
                            disabled={initialLoading}
                        >
                            {Array.isArray(getProductOptions()) && getProductOptions().map(product => (
                                <option key={product.value} value={product.value}>
                                    {product.label}
                                </option>
                            ))}
                        </select>

                        {timeReportList?.data?.topProductGraph?.length > 0 ? (
                            <div style={{ height: '320px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={timeReportList.data.topProductGraph}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="time" />
                                        <YAxis />
                                        <RechartsTooltip content={<LineCustomTooltip />} />
                                        <Legend />
                                        <Area
                                            type="monotone"
                                            dataKey="totalSale"
                                            fill="#10b981"
                                            fillOpacity={0.2}
                                            stroke="#10b981"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="totalSale"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            dot={{
                                                fill: "#10b981",
                                                stroke: "#fff",
                                                strokeWidth: 2,
                                                r: 5,
                                            }}
                                            activeDot={{
                                                r: 8,
                                                fill: "#059669",
                                                stroke: "#fff",
                                                strokeWidth: 2
                                            }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="no-data-message" style={{
                                height: '320px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '40px',
                                textAlign: 'center',
                                fontSize: '16px',
                                color: '#999',
                                borderRadius: '8px',
                                border: '1px dashed #ddd'
                            }}>No data available</div>
                        )}
                    </div>
                </div>

                {/* Hidden Print Component */}
                <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                    <PrintComponent 
                        ref={printRef}
                        reportData={timeReportList?.data}
                        filters={{
                            branchName: formValues.branch?.branchName || branchDetails?.branchName,
                            startDate: formValues.startDate,
                            endDate: formValues.endDate,
                            shift: shift?.shiftSeq,
                            timeRange: `${timeRange.start} - ${timeRange.end}`,
                            orderType: orderTypeOptions.find(opt => opt.value === orderType)?.label,
                            useShiftDates: useShiftDates
                        }}
                    />
                </div>

                {/* Hourly Sales Report Modal */}
                <HourlySalesReport
                    open={hourlySalesOpen}
                    onClose={() => {
                        console.log("Hourly Sales Data:", timeReportList?.data?.hourlyData);
                        console.log("Full API Response:", timeReportList?.data);
                        setHourlySalesOpen(false);
                    }}
                    data={timeReportList?.data?.hourlyData || []}
                    filters={{
                        date: `${formValues.startDate || ''} to ${formValues.endDate || ''}`,
                        branchName: formValues.branch?.branchName || branchDetails?.branchName || '',
                        timeRange: `${timeRange.start} - ${timeRange.end}`
                    }}
                    loading={loading}
                />
            </div>
        </div>
    );
}

export default TimeSalesReport;